import React, { useEffect } from 'react'
import { Col, Tabs, Typography, theme } from 'antd'
import { CalendarOutlined, LockOutlined } from '@ant-design/icons'
import CustomRow from '../../../reusable-components/CustomRow'
import ModalHeader from '../../../reusable-components/ModalHeader'
import AgiliteSkeleton from '../../../reusable-components/AgiliteSkeleton'
import dayjs from 'dayjs'

// Hooks
import { usePatientSearch } from './hooks/usePatientSearch'
import { useBookingState } from './hooks/useBookingState'
import { useAvailabilityBlocking } from './hooks/useAvailabilityBlocking'

// Components
import DateTimeSelection from './components/DateTimeSelection'
import DoctorSelection from './components/DoctorSelection'
import PatientSelection from './components/PatientSelection'
import ServiceSelection from './components/ServiceSelection'
import BookingFinalization from './components/BookingFinalization'
import BlockingTab from './components/BlockingTab'
import BlockedSlotDisplay from './components/BlockedSlotDisplay'

const { Title } = Typography
const { TabPane } = Tabs

const CalendarSlotAction = ({ event, setEvent, refreshFunction, selectedProfessional, medicalProfessionals }) => {
  const { token } = theme.useToken()

  // Hooks
  const patientSearch = usePatientSearch()
  const bookingState = useBookingState(event, selectedProfessional)
  const availabilityBlocking = useAvailabilityBlocking(event, bookingState, setEvent, refreshFunction)

  // Effects
  useEffect(() => {
    patientSearch.handlePatientSearch()
    // eslint-disable-next-line
  }, [patientSearch.searchQuery])

  useEffect(() => {
    if (bookingState.service && event?.extendedProps?.isAvailabilityEntry) {
      availabilityBlocking.validateSlotAvailability()
    }
    // eslint-disable-next-line
  }, [bookingState.service])

  // Render loading states
  if (bookingState.loading || availabilityBlocking.blockingActionBusy) {
    return (
      <>
        <ModalHeader title='Appointment Booking' handleReset={() => setEvent(null)} />
        <div style={{ padding: 16 }}>
          <AgiliteSkeleton
            skActive
            spinnerTip={
              availabilityBlocking.blockingActionBusy
                ? event?.extendedProps?.blocked
                  ? 'Unblocking slot...'
                  : 'Blocking Slot'
                : 'Finalizing booking...'
            }
          />
        </div>
      </>
    )
  }

  // Render blocked slot
  if (event?.extendedProps?.blocked) {
    return (
      <>
        <ModalHeader title='Appointment Booking' handleReset={() => setEvent(null)} />
        <CustomRow>
          <BlockedSlotDisplay
            event={event}
            blockingActionBusy={availabilityBlocking.blockingActionBusy}
            handleBlockout={availabilityBlocking.handleBlockout}
            token={token}
          />
        </CustomRow>
      </>
    )
  }

  return (
    <>
      <ModalHeader title='Appointment Booking' handleReset={() => setEvent(null)} />
      <CustomRow>
        <Col span={24}>
          <Tabs defaultActiveKey='booking' style={{ padding: '0 8px 8px' }} size='small' animated>
            <TabPane
              tab={
                <span>
                  <CalendarOutlined style={{ color: token.colorPrimary }} /> Booking
                </span>
              }
              key='booking'
            >
              <div style={{ padding: '4px 0' }}>
                <Title level={5} style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                  <CalendarOutlined style={{ marginRight: 6, color: token.colorPrimary }} />
                  {dayjs(bookingState.bookingDate).format('dddd, MMMM D, YYYY')}
                </Title>

                <CustomRow gutter={[8, 8]}>
                  <Col xs={24} md={12}>
                    <DateTimeSelection
                      bookingDate={bookingState.bookingDate}
                      setBookingDate={bookingState.setBookingDate}
                      bookingStart={bookingState.bookingStart}
                      setBookingStart={bookingState.setBookingStart}
                      event={event}
                      token={token}
                    />
                  </Col>

                  <Col xs={24} md={12}>
                    <DoctorSelection
                      selectedMedicalProfessional={bookingState.selectedMedicalProfessional}
                      setSelectedMedicalProfessional={bookingState.setSelectedMedicalProfessional}
                      setPatient={bookingState.setPatient}
                      setService={bookingState.setService}
                      medicalProfessionals={medicalProfessionals}
                      token={token}
                    />
                  </Col>

                  {bookingState.showPatientSelection && (
                    <Col xs={24} style={{ marginTop: '16px' }}>
                      <div
                        style={{
                          border: '1px solid #f0f0f0',
                          borderRadius: '6px',
                          backgroundColor: '#fff',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                      >
                        <div
                          style={{
                            padding: '6px 10px',
                            borderBottom: '1px solid #f0f0f0',
                            fontSize: '13px',
                            backgroundColor: '#fafafa',
                            borderTopLeftRadius: '6px',
                            borderTopRightRadius: '6px'
                          }}
                        >
                          Patient
                        </div>
                        <div style={{ padding: '8px 10px' }}>
                          <PatientSelection
                            patient={bookingState.patient}
                            setPatient={bookingState.setPatient}
                            searchResults={patientSearch.searchResults}
                            searching={patientSearch.searching}
                            searchQuery={patientSearch.searchQuery}
                            setSearchQuery={patientSearch.setSearchQuery}
                            handlePatientQuery={patientSearch.handlePatientQuery}
                            activePatientTab={bookingState.activePatientTab}
                            setActivePatientTab={bookingState.setActivePatientTab}
                            setService={bookingState.setService}
                            token={token}
                          />
                        </div>
                      </div>
                    </Col>
                  )}

                  {bookingState.showServiceSelection && (
                    <Col xs={24} style={{ marginTop: '24px' }}>
                      <ServiceSelection
                        service={bookingState.service}
                        setService={bookingState.setService}
                        selectedMedicalProfessional={bookingState.selectedMedicalProfessional}
                        token={token}
                      />
                    </Col>
                  )}

                  {availabilityBlocking.validating && (
                    <Col span={24}>
                      <AgiliteSkeleton skActive spinnerTip='Validating slot...' />
                    </Col>
                  )}

                  {bookingState.showFinalization && !availabilityBlocking.validating && (
                    <Col span={24}>
                      <BookingFinalization
                        bookingDetails={bookingState.bookingDetails}
                        setBookingDetails={bookingState.setBookingDetails}
                        onAfterSuccessfulBooking={() =>
                          bookingState.onAfterSuccessfulBooking(setEvent, refreshFunction)
                        }
                        patient={bookingState.patient}
                        setLoading={bookingState.setLoading}
                      />
                    </Col>
                  )}
                </CustomRow>
              </div>
            </TabPane>

            {event?.extendedProps?.isAvailabilityEntry && (
              <TabPane
                tab={
                  <span>
                    <LockOutlined style={{ color: token.colorPrimary }} /> Block/Unblock
                  </span>
                }
                key='block'
              >
                <BlockingTab
                  event={event}
                  reason={availabilityBlocking.reason}
                  setReason={availabilityBlocking.setReason}
                  blockingActionBusy={availabilityBlocking.blockingActionBusy}
                  handleBlockout={availabilityBlocking.handleBlockout}
                  showReasonModal={availabilityBlocking.showReasonModal}
                  setShowReasonModal={availabilityBlocking.setShowReasonModal}
                  token={token}
                />
              </TabPane>
            )}
          </Tabs>
        </Col>
      </CustomRow>
    </>
  )
}

export default CalendarSlotAction
